import { Application, Graphics, Container, Text, TextStyle } from 'pixi.js';

// Game constants
const GAME_WIDTH = 320;
const GAME_HEIGHT = 240;
const SCALE = 2;

// Game state
let score = 0;
let lives = 3;
let gameOver = false;
let shotsFired = 0;
let gameOverText = null;
let restartTimeout = null;
let shooterFreq = 0;

// Game objects
let player;
let bullets = [];
let enemies = [];
let enemyGroups = [];
let currentFormation = 0;
let formationPatterns = ['diamond', 'clusters', 'vshape', 'staggered'];
let enemyDirection = 1;
let enemySpeed = 1;
let lastShotTime = 0;
const SHOT_COOLDOWN = 300; // ms

// Key repetition system
let keyStates = {}

function initKeyStates() {
  keyStates = {
    ArrowLeft: { pressed: false, repeatTimer: 0, initialDelay: true },
    ArrowRight: { pressed: false, repeatTimer: 0, initialDelay: true }
  };
}


// Touch state tracking
let touchLeftActive = false;
let touchRightActive = false;

const INITIAL_REPEAT_DELAY = 400; // ms
const REPEAT_INTERVAL = 60; // ms

// Enemy bullet system
let enemyBullets = [];
let enemyShooters = []; // Array of enemy objects that can shoot
const ENEMY_SHOOT_INTERVAL = 1500; // ms between shots per shooter
const ENEMY_BULLET_SPEED = 5;
let playerInvincible = false;
let playerInvincibleUntil = 0;
const PLAYER_INVINCIBILITY_TIME = 1000; // ms

// Weapon system
let aoeBullets = [];
let explosions = [];
const EXPLOSION_RADIUS = 40;

// Charge shot
let isCharging = false;
let chargeStartTime = 0;
let chargeLevel = 0; // 0-100%
let chargeIndicator = null;

// UI elements
let scoreText;
let livesText;

// Initialize PixiJS application
const app = new Application();

// Create game container (will be added to stage after app.init)
const game = new Container();

// Initialize game
async function init() {
  // Initialize PixiJS app with configuration
  await app.init({
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    backgroundColor: 0x000000,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
    antialias: false,
  });

  initKeyStates();

  // Scale up for pixel art
  app.canvas.style.width = `${GAME_WIDTH * SCALE}px`;
  app.canvas.style.height = `${GAME_HEIGHT * SCALE}px`;
  app.canvas.style.imageRendering = 'pixelated';

  // Get the canvas element and replace it with Pixi's canvas
  const gameCanvas = document.getElementById('game-canvas');
  const gameContainer = document.getElementById('game-container');
  gameCanvas.remove();
  gameContainer.appendChild(app.canvas);

  // Add game container to stage
  app.stage.addChild(game);

  // Create game objects
  createPlayer();
  createChargeIndicator();
  createFormation(formationPatterns[currentFormation]);
  createUI();

  // Start game loop
  app.ticker.add(gameLoop);

  // Handle input
  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('keyup', handleKeyUp);
  setupTouchControls();
}

// Create player ship
function createPlayer() {
  player = new Graphics();
  player.beginFill(0x00FF00);
  player.drawRect(-10, -5, 20, 10);
  player.drawRect(-5, -10, 10, 5);
  player.endFill();
  player.x = GAME_WIDTH / 2;
  player.y = GAME_HEIGHT - 30;
  player.tint = 0x00FF00; // default green
  game.addChild(player);
}

// Create enemy formation (replaces dense grid)
function createFormation(patternType) {
  enemies = [];
  enemyGroups = [];

  const colors = [0xFF0088, 0xFF8800, 0xFFFF00, 0x00FF00];
  const color = colors[currentFormation % colors.length];

  switch (patternType) {
    case 'vshape':
      // V-shaped formation: 12 enemies
      const vPoints = [
        [160, 30], [140, 50], [180, 50],
        [120, 70], [200, 70],
        [100, 90], [220, 90],
        [80, 110], [240, 110],
        [60, 130], [260, 130],
        [40, 150]
      ];
      vPoints.forEach(([x, y]) => {
        const enemy = new Graphics();
        enemy.beginFill(0xFFFFFF);
        enemy.drawRect(-8, -8, 16, 16);
        enemy.drawRect(-12, -4, 24, 8);
        enemy.endFill();
        enemy.tint = color;
        enemy.baseTint = color;
        enemy.x = x;
        enemy.y = y;
        game.addChild(enemy);
        enemies.push(enemy);
      });
      // Single group for V shape
      enemyGroups.push({ enemies: [...enemies], x: 0, y: 0, speed: enemySpeed, direction: enemyDirection });
      break;

    case 'diamond':
      // Diamond formation: 16 enemies
      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 4; j++) {
          if (Math.abs(i - 1.5) + Math.abs(j - 1.5) <= 2.5) {
            const enemy = new Graphics();
            enemy.beginFill(0xFFFFFF);
            enemy.drawRect(-8, -8, 16, 16);
            enemy.drawRect(-12, -4, 24, 8);
            enemy.endFill();
            enemy.tint = color;
            enemy.baseTint = color;
            enemy.x = 100 + j * 40;
            enemy.y = 40 + i * 30;
            game.addChild(enemy);
            enemies.push(enemy);
          }
        }
      }
      enemyGroups.push({ enemies: [...enemies], x: 0, y: 0, speed: enemySpeed, direction: enemyDirection });
      break;

    case 'clusters':
      // Scattered clusters: 5 groups of 3-4 enemies each
      const clusters = [
        { x: 60, y: 40, count: 3 },
        { x: 260, y: 60, count: 4 },
        { x: 100, y: 100, count: 3 },
        { x: 220, y: 120, count: 4 },
        { x: 160, y: 160, count: 3 },
        { x: 180, y: 90, count: 6 },
      ];
      clusters.forEach((cluster, idx) => {
        const groupEnemies = [];
        for (let i = 0; i < cluster.count; i++) {
          const enemy = new Graphics();
          enemy.beginFill(0xFFFFFF);
          enemy.drawRect(-8, -8, 16, 16);
          enemy.drawRect(-12, -4, 24, 8);
          enemy.endFill();
          enemy.tint = color;
          enemy.baseTint = color;
          enemy.x = cluster.x + (i % 2) * 20 - 10;
          enemy.y = cluster.y + Math.floor(i / 2) * 20 - 10;
          game.addChild(enemy);
          enemies.push(enemy);
          groupEnemies.push(enemy);
        }
        enemyGroups.push({ enemies: groupEnemies, x: cluster.x, y: cluster.y, speed: enemySpeed * (idx % 2 === 0 ? 1 : 1.5), direction: idx % 2 === 0 ? 1 : -1 });
      });
      break;

    case 'staggered':
      // Staggered rows: 4 rows of 6 enemies
      for (let row = 0; row < 4; row++) {
        let rowEnemies = 0;
        for (let col = 0; col < 6; col++) {
          if (rowEnemies > 3 || Math.random() > 0.3) {
            continue
          }
          rowEnemies += 1;
          const enemy = new Graphics();
          enemy.beginFill(0xFFFFFF);
          enemy.drawRect(-8, -8, 16, 16);
          enemy.drawRect(-12, -4, 24, 8);
          enemy.endFill();
          enemy.tint = color;
          enemy.baseTint = color;
          enemy.x = 50 + col * 40 + (row % 2) * 20;
          enemy.y = 40 + row * 30;
          game.addChild(enemy);
          enemies.push(enemy);
        }
      }
      enemyGroups.push({ enemies: [...enemies], x: 0, y: 0, speed: 0.9 * enemySpeed, direction: enemyDirection });
      break;

    default:
      // Fallback to original grid (smaller)
      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 6; col++) {
          const enemy = new Graphics();
          enemy.beginFill(0xFFFFFF);
          enemy.drawRect(-8, -8, 16, 16);
          enemy.drawRect(-12, -4, 24, 8);
          enemy.endFill();
          enemy.tint = 0xFF0000;
          enemy.baseTint = 0xFF0000;
          enemy.x = 70 + col * 30;
          enemy.y = 40 + row * 25;
          game.addChild(enemy);
          enemies.push(enemy);
        }
      }
      enemyGroups.push({ enemies: [...enemies], x: 0, y: 0, speed: enemySpeed, direction: enemyDirection });
  }
  clearShooterEnemies();
  identifyShooterEnemies();
}

// Identify shooter enemies (bottom-most in each column)
function clearShooterEnemies() {
  // Clear previous shooter marks
  enemies.forEach(e => delete e.canShoot);
  // Reset tints to base color
  enemies.forEach(e => {
     if (e.baseTint === undefined) e.baseTint = e.tint;
     e.tint = e.baseTint;
  });
  enemyShooters = [];
}

function identifyShooterEnemies() {
  // Group enemies by approximate column (bucket x positions)
  const columnWidth = 40; // Approximate column width
  const columnMap = new Map();

  enemies.forEach(enemy => {
    const col = Math.floor(enemy.x / columnWidth);
    if (!columnMap.has(col) || columnMap.get(col).y < enemy.y) {
      // This enemy is lower (higher y) than current column candidate
      columnMap.set(col, enemy);
    }
  });

  const now = Date.now();
  // Mark shooter enemies
  columnMap.forEach(enemy => {
    if (enemy.canShoot || Math.random() > shooterFreq) return;
    enemy.canShoot = true;
    enemy.tint = 0xFF0000; // Red for shooter
    enemy.lastShotTime = now;
    enemyShooters.push(enemy);
  });
}

// Create UI
function createUI() {
  const style = new TextStyle({
    fontFamily: 'monospace',
    fontSize: 12,
    fill: 0x00FF00,
  });

  scoreText = new Text('SCORE: 0000', style);
  scoreText.x = 10;
  scoreText.y = 10;
  app.stage.addChild(scoreText);

  livesText = new Text('LIVES: 3', style);
  livesText.x = GAME_WIDTH - 80;
  livesText.y = 10;
  app.stage.addChild(livesText);
}

// Create charge indicator
function createChargeIndicator() {
  chargeIndicator = new Graphics();
  chargeIndicator.beginFill(0x00FF00);
  chargeIndicator.drawRect(-20, 5, 40, 6);
  chargeIndicator.endFill();
  chargeIndicator.x = player.x;
  chargeIndicator.y = player.y + 15;
  game.addChild(chargeIndicator);
}

// Update UI
function updateUI() {
  scoreText.text = `SCORE: ${score.toString().padStart(4, '0')}`;
  livesText.text = `LIVES: ${lives}`;
}

// Update charge indicator
function updateChargeIndicator() {
  if (!chargeIndicator) return;

  // Update position to follow player
  chargeIndicator.x = player.x;
  chargeIndicator.y = player.y + 15;

  // Update width based on charge level
  chargeIndicator.clear();
  let color = 0x00FF00; // green
  if (chargeLevel > 0.66) color = 0xFF0000; // red
  else if (chargeLevel > 0.33) color = 0xFFFF00; // yellow

  chargeIndicator.beginFill(color);
  chargeIndicator.drawRect(-20, 5, 40 * chargeLevel, 6);
  chargeIndicator.endFill();

  // Also update charge level if charging
  if (isCharging) {
    chargeLevel = Math.min((Date.now() - chargeStartTime) / 1000, 1.0);
  }
}

// Handle keyboard input
function handleKeyDown(event) {
  if (gameOver) return;

  switch(event.key) {
    case 'ArrowLeft':
      if (keyStates.ArrowLeft.pressed) return;
      keyStates.ArrowLeft.pressed = true;
      keyStates.ArrowLeft.repeatTimer = 0;
      keyStates.ArrowLeft.initialDelay = true;
      // Immediate movement for first keypress
      player.x = Math.max(player.x - 5, 20);
      break;
    case 'ArrowRight':
      if (keyStates.ArrowRight.pressed) return;
      keyStates.ArrowRight.pressed = true;
      keyStates.ArrowRight.repeatTimer = 0;
      keyStates.ArrowRight.initialDelay = true;
      player.x = Math.min(player.x + 5, GAME_WIDTH - 20);
      break;
    case ' ':
      // Start charging shot
      if (!isCharging) {
        isCharging = true;
        chargeStartTime = Date.now();
        chargeLevel = 0;
      }
      break;
  }
}

// Handle key release (for charge shot)
function handleKeyUp(event) {
  if (gameOver) return;

  switch(event.key) {
    case 'ArrowLeft':
      keyStates.ArrowLeft.pressed = false;
      break;
    case 'ArrowRight':
      keyStates.ArrowRight.pressed = false;
      break;
    case ' ':
      if (isCharging) {
        isCharging = false;
        // Determine charge level
        const chargeTime = Date.now() - chargeStartTime;
        chargeLevel = Math.min(chargeTime / 1000, 1.0); // 1000ms for full charge

        // Fire based on charge level
        if (chargeLevel < 0.33) {
          shoot(0); // Level 1: normal bullet (respects weapon mode)
        } else if (chargeLevel < 0.66) {
          shoot(1); // Level 2: faster blue bullet
        } else {
          shoot(2); // Level 3: AOE bullet
        }
        chargeLevel = 0;
      }
      break;
  }
}

// Update player movement with key repetition
function updatePlayerMovement() {
  const canMove = !isCharging || (chargeLevel === 1.0 )
  const deltaTime = app.ticker.deltaMS;

  // Left arrow movement
  if (keyStates.ArrowLeft.pressed) {
    keyStates.ArrowLeft.repeatTimer += deltaTime;
    const delay = keyStates.ArrowLeft.initialDelay ? INITIAL_REPEAT_DELAY : REPEAT_INTERVAL;

    if (keyStates.ArrowLeft.repeatTimer >= delay) {
      if (canMove) { player.x = Math.max(player.x - 5, 20); }
      keyStates.ArrowLeft.repeatTimer = 0;
      keyStates.ArrowLeft.initialDelay = false;
    }
  }

  // Right arrow movement (similar logic)
  if (keyStates.ArrowRight.pressed) {
    keyStates.ArrowRight.repeatTimer += deltaTime;
    const delay = keyStates.ArrowRight.initialDelay ? INITIAL_REPEAT_DELAY : REPEAT_INTERVAL;

    if (keyStates.ArrowRight.repeatTimer >= delay) {
      if (canMove) { player.x = Math.min(player.x + 5, GAME_WIDTH - 20); }
      keyStates.ArrowRight.repeatTimer = 0;
      keyStates.ArrowRight.initialDelay = false;
    }
  }
}

// Setup touch controls for mobile
function setupTouchControls() {
  const touchLeft = document.getElementById('touch-left');
  const touchRight = document.getElementById('touch-right');
  const gameContainer = document.getElementById('game-container');

  if (!touchLeft || !touchRight || !gameContainer) return;

  // Left button touch handlers
  touchLeft.addEventListener('touchstart', (e) => {
    e.preventDefault();
    touchLeftActive = true;
    keyStates.ArrowLeft.pressed = true;
    keyStates.ArrowLeft.repeatTimer = 0;
    keyStates.ArrowLeft.initialDelay = true;
    // Immediate movement for first touch
    player.x = Math.max(player.x - 5, 20);
  });

  touchLeft.addEventListener('touchend', (e) => {
    e.preventDefault();
    touchLeftActive = false;
    keyStates.ArrowLeft.pressed = false;
  });
  touchLeft.addEventListener('touchcancel', (e) => {
    e.preventDefault();
    touchLeftActive = false;
    keyStates.ArrowLeft.pressed = false;
  });
  // Left button touch handlers
  touchLeft.addEventListener('mousedown', (e) => {
    e.preventDefault();
    touchLeftActive = true;
    keyStates.ArrowLeft.pressed = true;
    keyStates.ArrowLeft.repeatTimer = 0;
    keyStates.ArrowLeft.initialDelay = true;
    // Immediate movement for first touch
    player.x = Math.max(player.x - 5, 20);
  });

  touchLeft.addEventListener('mouseup', (e) => {
    e.preventDefault();
    touchLeftActive = false;
    keyStates.ArrowLeft.pressed = false;
  });

  // Right button touch handlers (similar)
  touchRight.addEventListener('touchstart', (e) => {
    e.preventDefault();
    touchRightActive = true;
    keyStates.ArrowRight.pressed = true;
    keyStates.ArrowRight.repeatTimer = 0;
    keyStates.ArrowRight.initialDelay = true;
    player.x = Math.min(player.x + 5, GAME_WIDTH - 20);
  });

  touchRight.addEventListener('touchend', (e) => {
    e.preventDefault();
    touchRightActive = false;
    keyStates.ArrowRight.pressed = false;
  });
  touchRight.addEventListener('touchcancel', (e) => {
    e.preventDefault();
    touchRightActive = false;
    keyStates.ArrowRight.pressed = false;
  });
  touchRight.addEventListener('mousedown', (e) => {
    e.preventDefault();
    touchRightActive = true;
    keyStates.ArrowRight.pressed = true;
    keyStates.ArrowRight.repeatTimer = 0;
    keyStates.ArrowRight.initialDelay = true;
    player.x = Math.min(player.x + 5, GAME_WIDTH - 20);
  });

  touchRight.addEventListener('mouseup', (e) => {
    e.preventDefault();
    touchRightActive = false;
    keyStates.ArrowRight.pressed = false;
  });

  // Main screen touch for charging (like spacebar)
  let touchStartTime = 0;

  gameContainer.addEventListener('touchstart', (e) => {
    e.preventDefault();
    // Only handle touches not on buttons
    if (e.target === gameContainer || e.target === app.canvas) {
      if (!isCharging) {
        isCharging = true;
        chargeStartTime = Date.now();
        chargeLevel = 0;
      }
    }
  });

  gameContainer.addEventListener('touchend', (e) => {
    e.preventDefault();
    if (isCharging) {
      isCharging = false;
      const chargeTime = Date.now() - chargeStartTime;
      chargeLevel = Math.min(chargeTime / 1000, 1.0);

      // Fire based on charge level (existing logic from handleKeyUp)
      if (chargeLevel < 0.33) {
        shoot(0);
      } else if (chargeLevel < 0.66) {
        shoot(1);
      } else {
        shoot(2);
      }
      chargeLevel = 0;
    }
  });
  gameContainer.addEventListener('touchcancel', (e) => {
    e.preventDefault();
    if (isCharging) {
      isCharging = false;
      const chargeTime = Date.now() - chargeStartTime;
      chargeLevel = Math.min(chargeTime / 1000, 1.0);

      // Fire based on charge level (existing logic from handleKeyUp)
      if (chargeLevel < 0.33) {
        shoot(0);
      } else if (chargeLevel < 0.66) {
        shoot(1);
      } else {
        shoot(2);
      }
      chargeLevel = 0;
    }
  });

  // Prevent context menu on long press
  gameContainer.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    return false;
  });
}

// Create bullet
function shoot(chargeLevel = 0) {
  const now = Date.now();
  if (now - lastShotTime < SHOT_COOLDOWN) return;
  lastShotTime = now;
  shotsFired++;

  const bullet = new Graphics();
  let speed = 8;
  let color = 0xFFFF00; // yellow default

  if (chargeLevel === 1) {
    // Level 2 charge: faster blue bullet
    color = 0x0088FF;
    speed = 12;
    bullet.drawRect(-3, -10, 6, 20);
    bullet.isFaster = true;
  } else if (chargeLevel === 2) {
    // Level 3 charge: AOE bullet (orange)
    color = 0xFF8800;
    speed = 6;
    bullet.drawRect(-4, -12, 8, 24);
    // Store as AOE bullet
    bullet.isAoe = true;
    aoeBullets.push(bullet);
  } else {
    // Normal bullet
    bullet.drawRect(-2, -8, 4, 16);
  }

  bullet.beginFill(color);
  bullet.endFill();
  bullet.x = player.x;
  bullet.y = player.y - 10;
  bullet.speed = speed;
  game.addChild(bullet);
  bullets.push(bullet);
}

// Enemy shooting function
function enemyShoot(shooter) {
  const now = Date.now();
  if (now - shooter.lastShotTime < ENEMY_SHOOT_INTERVAL) return;

  const bullet = new Graphics();
  bullet.beginFill(0xFF0000); // Red enemy bullet
  bullet.drawRect(-3, 0, 6, 12); // Different shape: pointing down
  bullet.endFill();

  bullet.x = shooter.x;
  bullet.y = shooter.y + 15; // Start below enemy
  bullet.speed = ENEMY_BULLET_SPEED;
  bullet.isEnemyBullet = true;

  game.addChild(bullet);
  enemyBullets.push(bullet);
  shooter.lastShotTime = now;
}

// Create AOE bullet (for toggle mode)
function createAoeBullet(x, y) {
  const bullet = new Graphics();
  bullet.beginFill(0xFF8800);
  bullet.drawRect(-4, -12, 8, 24);
  bullet.endFill();
  bullet.x = x;
  bullet.y = y;
  bullet.speed = 6;
  bullet.isAoe = true;
  game.addChild(bullet);
  aoeBullets.push(bullet);
}

// Update bullets
function updateBullets() {
  // Process regular bullets
  for (let i = bullets.length - 1; i >= 0; i--) {
    const bullet = bullets[i];
    bullet.y -= bullet.speed || 8;

    // Remove if off screen
    if (bullet.y < -10) {
      game.removeChild(bullet);
      // Remove from aoeBullets if applicable
      if (bullet.isAoe) {
        const aoeIndex = aoeBullets.indexOf(bullet);
        if (aoeIndex > -1) aoeBullets.splice(aoeIndex, 1);
      }
      bullets.splice(i, 1);
      continue;
    }

    // Check collision with enemies
    for (let j = enemies.length - 1; j >= 0; j--) {
      const enemy = enemies[j];
      if (bullet.x > enemy.x - 10 && bullet.x < enemy.x + 10 &&
          bullet.y > enemy.y - 10 && bullet.y < enemy.y + 10) {
        // Hit!
        removeEnemy(enemy);
        if (!bullet.isFaster) {
          game.removeChild(bullet);
          bullets.splice(i, 1);
	}
        score += 100;
        updateUI();

        // If AOE bullet, create explosion
        if (bullet.isAoe) {
          createExplosionEffect(bullet.x, bullet.y);
          checkExplosionCollision(bullet.x, bullet.y, EXPLOSION_RADIUS);
          // Remove from aoeBullets array
          const aoeIndex = aoeBullets.indexOf(bullet);
          if (aoeIndex > -1) aoeBullets.splice(aoeIndex, 1);
        }
        break;
      }
    }
  }

  // Process AOE bullets that missed enemies but reached bottom? They'll be removed off-screen above.
}

// Update enemy bullets
function updateEnemyBullets() {
  for (let i = enemyBullets.length - 1; i >= 0; i--) {
    const bullet = enemyBullets[i];
    bullet.y += bullet.speed; // Move downward

    // Remove if off screen
    if (bullet.y > GAME_HEIGHT + 10) {
      game.removeChild(bullet);
      enemyBullets.splice(i, 1);
      continue;
    }

    // Check collision with player
    if (!playerInvincible && bullet.x > player.x - 15 && bullet.x < player.x + 15 &&
        bullet.y > player.y - 10 && bullet.y < player.y + 10) {
      // Hit player!
      game.removeChild(bullet);
      enemyBullets.splice(i, 1);
      hitPlayer();
      continue;
    }
  }
}

// Player hit by enemy bullet
function hitPlayer() {
  if (playerInvincible) return;

  lives--;
  updateUI();

  // Set invincibility
  playerInvincible = true;
  playerInvincibleUntil = Date.now() + PLAYER_INVINCIBILITY_TIME;

  if (lives <= 0) {
    gameOver = true;
    showGameOver();
  } else {
    // Visual feedback: flash player
    player.alpha = 0.5;
    setTimeout(() => { player.alpha = 1.0; }, 200);
  }
}

function updatePlayerInvincibility() {
  if (playerInvincible && Date.now() > playerInvincibleUntil) {
    playerInvincible = false;
    player.alpha = 1.0;
  }
}

// Check explosion radius damage
function checkExplosionCollision(centerX, centerY, radius) {
  for (let j = enemies.length - 1; j >= 0; j--) {
    const enemy = enemies[j];
    const dx = enemy.x - centerX;
    const dy = enemy.y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance <= radius) {
      removeEnemy(enemy);
      score += 100;
      updateUI();
    }
  }
}

// Create visual explosion effect
function createExplosionEffect(x, y) {
  const explosion = new Graphics();
  explosion.beginFill(0xFFFFFF);
  explosion.drawCircle(0, 0, 5);
  explosion.endFill();
  explosion.x = x;
  explosion.y = y;
  explosion.radius = 5;
  explosion.maxRadius = 30;
  game.addChild(explosion);
  explosions.push(explosion);
}

// Update explosion animations
function updateExplosions() {
  for (let i = explosions.length - 1; i >= 0; i--) {
    const explosion = explosions[i];
    explosion.radius += 2;
    explosion.clear();
    explosion.beginFill(0xFFFF00, 0.7);
    explosion.drawCircle(0, 0, explosion.radius);
    explosion.endFill();
    if (explosion.radius > explosion.maxRadius) {
      game.removeChild(explosion);
      explosions.splice(i, 1);
    }
  }
}

// Remove enemy from game and groups
function removeEnemy(enemy) {
  // Remove from enemies array
  const index = enemies.indexOf(enemy);
  if (index > -1) enemies.splice(index, 1);

  // Remove from enemyShooters if present
  const shooterIndex = enemyShooters.indexOf(enemy);
  if (shooterIndex > -1) {
    enemyShooters.splice(shooterIndex, 1);
  }

  // Remove from all groups
  for (const group of enemyGroups) {
    const groupIndex = group.enemies.indexOf(enemy);
    if (groupIndex > -1) {
      group.enemies.splice(groupIndex, 1);
    }
  }

  // Remove graphics from game
  game.removeChild(enemy);

}

// Update enemies
function updateEnemies() {
  // Move enemies based on groups
  for (const group of enemyGroups) {
    let groupEdgeReached = false;
    for (const enemy of group.enemies) {
      enemy.x += group.speed * group.direction;

      // Check if any enemy in group reached screen edge
      if (enemy.x < 20 || enemy.x > GAME_WIDTH - 20) {
        groupEdgeReached = true;
      }

      // Check if enemy reached bottom
      if (enemy.y > GAME_HEIGHT - 40) {
        gameOver = true;
        showGameOver();
        return;
      }
    }

    // Reverse group direction and move down if edge reached
    if (groupEdgeReached) {
      group.direction *= -1;
      for (const enemy of group.enemies) {
        enemy.y += 10;
      }
      // Increase group speed slightly
      group.speed += 0.1;
      identifyShooterEnemies();
    }
  }

  // Update shooter enemies
  const now = Date.now();
  enemyShooters.forEach(shooter => {
    if (shooter.canShoot && now - shooter.lastShotTime >= ENEMY_SHOOT_INTERVAL) {
      enemyShoot(shooter);
    }
  });

  // Check if all enemies defeated
  if (enemies.length === 0) {
    currentFormation = (currentFormation + 1) % formationPatterns.length;
    createFormation(formationPatterns[currentFormation]);
    enemySpeed += 0.01;
    shooterFreq += 0.03;
    // Reset groups speed/direction? They will be recreated anyway.
  }
}

// Game over
function showGameOver() {
  // Remove existing game over text
  if (gameOverText) {
    app.stage.removeChild(gameOverText);
    gameOverText = null;
  }

  // Hide restart button if visible
  hideRestartButton();

  const style = new TextStyle({
    fontFamily: 'monospace',
    fontSize: 24,
    fill: 0xFF0000,
  });

  gameOverText = new Text('GAME OVER', style);
  gameOverText.x = GAME_WIDTH / 2;
  gameOverText.y = GAME_HEIGHT / 2;
  gameOverText.anchor.set(0.5);
  app.stage.addChild(gameOverText);

  // Show restart button after 0.5sec
  if (restartTimeout) clearTimeout(restartTimeout);
  restartTimeout = setTimeout(showRestartButton, 500);
}

// Show restart button and hint
function showRestartButton() {
  // Clear the timeout reference since it has fired
  if (restartTimeout) {
    restartTimeout = null;
  }
  const ui = document.getElementById('game-over-ui');
  const restartBtn = document.getElementById('restart-btn');
  const fireHint = document.getElementById('fire-hint');
  const scoreValue = document.getElementById('score-value');
  if (!ui || !restartBtn || !fireHint) return;

  scoreValue.textContent = score;

  ui.classList.remove('hidden');
  ui.classList.add('visible');

  // Show fire hint if no shots were fired
  if (shotsFired === 0) {
    fireHint.classList.remove('hidden');
  } else {
    fireHint.classList.add('hidden');
  }

  // Attach click event to restart button
  restartBtn.onclick = restartGame;
}

function hideRestartButton() {
  if (restartTimeout) {
    clearTimeout(restartTimeout);
    restartTimeout = null;
  }
  const ui = document.getElementById('game-over-ui');
  if (ui) {
    ui.classList.add('hidden');
    ui.classList.remove('visible');
  }
}

function restartGame() {
  if (!gameOver) return;
  // Hide restart button
  hideRestartButton();

  initKeyStates();

  // Remove game over text
  if (gameOverText) {
    app.stage.removeChild(gameOverText);
    gameOverText = null;
  }

  // Reset game state
  score = 0;
  lives = 3;
  shooterFreq = 0;
  gameOver = false;
  shotsFired = 0;

  // Clear all game objects
  bullets.forEach(bullet => game.removeChild(bullet));
  bullets = [];
  enemyBullets.forEach(bullet => game.removeChild(bullet));
  enemyBullets = [];
  aoeBullets.forEach(bullet => game.removeChild(bullet));
  aoeBullets = [];
  explosions.forEach(explosion => game.removeChild(explosion));
  explosions = [];

  // Remove enemies
  enemies.forEach(enemy => game.removeChild(enemy));
  enemies = [];
  enemyGroups = [];
  enemyShooters = [];

  // Reset player
  player.x = GAME_WIDTH / 2;
  player.y = GAME_HEIGHT - 30;
  player.tint = 0x00FF00;
  player.alpha = 1.0;
  playerInvincible = false;
  playerInvincibleUntil = 0;

  // Reset charge state
  isCharging = false;
  chargeLevel = 0;

  // Create new formation
  currentFormation = 0;
  enemySpeed = 1;
  createFormation(formationPatterns[currentFormation]);

  // Update UI
  updateUI();
}

// Main game loop
function gameLoop() {
  if (gameOver) return;

  updatePlayerMovement();
  updateBullets();
  updateEnemyBullets();
  updateEnemies();
  updateChargeIndicator();
  updatePlayerInvincibility();
  updateExplosions();
}

// Start the game
(async () => {
  await init();
  updateUI();

  // Initial resize
  window.dispatchEvent(new Event('resize'));
})();

// Handle window resize
window.addEventListener('resize', () => {
  // Guard: app.canvas might not be initialized yet
  if (!app.canvas) return;

  // Keep aspect ratio
  const scale = Math.min(
    window.innerWidth / GAME_WIDTH,
    window.innerHeight / GAME_HEIGHT
  ) * 0.8;

  app.canvas.style.width = `${GAME_WIDTH * scale}px`;
  app.canvas.style.height = `${GAME_HEIGHT * scale}px`;
});

